export const DEFAULT_BILLING_CONFIG = {
    basePriceCents: 2900,
    currency: 'usd',
    offerEnabled: false,
    offerLabel: 'Limited-time offer',
    offerCode: '',
    discountType: 'percent',
    discountValue: 0,
    offerStartsAt: null,
    offerEndsAt: null,
    updatedAt: null,
    updatedBy: null,
};

const MIN_PRICE_CENTS = 50;
const MAX_PRICE_CENTS = 1_000_000;

export const toBillingDate = (value) => {
    if (!value) return null;
    try {
        if (typeof value.toDate === 'function') return value.toDate();
        if (value instanceof Date) return value;
        if (typeof value === 'object' && typeof value.seconds === 'number') {
            return new Date(value.seconds * 1000 + Math.floor((value.nanoseconds || 0) / 1e6));
        }
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    } catch (error) {
        return null;
    }
};

const numberOr = (value, fallback) => {
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

export const normalizeBillingConfig = (raw = {}) => {
    const source = raw && typeof raw === 'object' ? raw : {};
    const basePriceCents = Math.min(
        MAX_PRICE_CENTS,
        Math.max(MIN_PRICE_CENTS, Math.round(numberOr(source.basePriceCents, DEFAULT_BILLING_CONFIG.basePriceCents))),
    );
    const discountType = source.discountType === 'fixed' ? 'fixed' : 'percent';
    const maxDiscount = Math.max(0, basePriceCents - MIN_PRICE_CENTS);
    const rawDiscount = Math.max(0, numberOr(source.discountValue, 0));
    const discountValue = discountType === 'fixed'
        ? Math.min(maxDiscount, Math.round(rawDiscount))
        : Math.min(99, Math.round(rawDiscount));

    return {
        ...DEFAULT_BILLING_CONFIG,
        ...source,
        basePriceCents,
        currency: 'usd',
        offerEnabled: source.offerEnabled === true,
        offerLabel: typeof source.offerLabel === 'string' ? source.offerLabel.trim().slice(0, 80) : DEFAULT_BILLING_CONFIG.offerLabel,
        offerCode: typeof source.offerCode === 'string' ? source.offerCode.trim().toUpperCase().slice(0, 40) : '',
        discountType,
        discountValue,
        offerStartsAt: toBillingDate(source.offerStartsAt),
        offerEndsAt: toBillingDate(source.offerEndsAt),
        updatedAt: toBillingDate(source.updatedAt),
        updatedBy: typeof source.updatedBy === 'string' ? source.updatedBy : null,
    };
};

const isOfferInWindow = (config, now) => {
    const currentTime = typeof now === 'number' ? now : Date.now();
    const startsAt = config.offerStartsAt?.getTime();
    const endsAt = config.offerEndsAt?.getTime();
    return config.offerEnabled
        && config.discountValue > 0
        && (!startsAt || currentTime >= startsAt)
        && (!endsAt || currentTime < endsAt)
        && (!startsAt || !endsAt || endsAt > startsAt);
};

const discountFor = (config) => {
    if (config.discountType === 'fixed') {
        return Math.min(config.discountValue, config.basePriceCents - MIN_PRICE_CENTS);
    }
    return Math.min(
        config.basePriceCents - MIN_PRICE_CENTS,
        Math.round(config.basePriceCents * (config.discountValue / 100)),
    );
};

export const getBillingPricing = (rawConfig, enteredCode = '', now = Date.now()) => {
    const config = normalizeBillingConfig(rawConfig);
    const normalizedCode = typeof enteredCode === 'string' ? enteredCode.trim().toUpperCase() : '';
    const offerActive = isOfferInWindow(config, now);
    const codeRequired = offerActive && Boolean(config.offerCode);
    const codeMatches = !codeRequired || normalizedCode === config.offerCode;
    const discountCents = offerActive && codeMatches ? discountFor(config) : 0;

    return {
        ...config,
        offerActive,
        codeRequired,
        codeMatches,
        isValid: !codeRequired || codeMatches,
        discountCents,
        finalPriceCents: Math.max(MIN_PRICE_CENTS, config.basePriceCents - discountCents),
        savingsLabel: discountCents > 0
            ? config.discountType === 'percent'
                ? `${config.discountValue}% off`
                : `${formatBillingPrice(discountCents)} off`
            : '',
        basePriceLabel: formatBillingPrice(config.basePriceCents, config.currency),
        finalPriceLabel: formatBillingPrice(
            Math.max(MIN_PRICE_CENTS, config.basePriceCents - discountCents),
            config.currency,
        ),
    };
};

export const formatBillingPrice = (cents, currency = 'usd') => {
    const safeCents = Math.max(0, Math.round(numberOr(cents, 0)));
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: String(currency || 'usd').toUpperCase(),
        minimumFractionDigits: safeCents % 100 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
    }).format(safeCents / 100);
};

